import React, { useEffect, useState } from 'react';
import {useSearchParams} from 'react-router-dom';
import { parseBackendDate } from '../../utils/dateFormatter';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import AdvancedFilters from '../../components/common/AdvancedFilters';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import AddTicketModal from '../../components/features/tickets/AddTicketModal';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import ticketService from '../../services/ticketService';
import engineerService from '../../services/engineerService';
import siteService from '../../services/siteService';
import { formatDateTime } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';

const TicketsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [engineers, setEngineers] = useState([]);
  const [sites, setSites] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const status = searchParams.get('status');
    const priority = searchParams.get('priority');
    const closedToday = searchParams.get('closedToday');
    const activeOnly = searchParams.get('activeOnly')
    const overdue = searchParams.get('overdue')
    if (status === 'CLOSED' && closedToday) return { status: 'CLOSED', closedToday: true };
    if (priority) return { priority, activeOnly: activeOnly === 'true' };
    if (status) return { status };
    if(overdue) return {overdue : true}
    return {};
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadTickets();
    loadEngineers();
    loadSites();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [searchTerm, advancedFilters, tickets]);

  const loadTickets = async () => {
    setLoading(true);
    const result = await ticketService.getAllTickets();
    if (result.success) {
      setTickets(result.data);
      setFilteredTickets(result.data);
    }
    setLoading(false);
  };

  const loadEngineers = async () => {
    const result = await engineerService.getAllEngineers();
    if (result.success) {
      setEngineers(result.data);
    }
  };

  const loadSites = async () => {
    const result = await siteService.getAllSites();
    if (result.success) {
      setSites(result.data.map(site => ({ value: site.id.toString(), label: `${site.name} - ${site.location}` })));
    } else {
      console.error('Failed to load sites:', result.error);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.id.toString().includes(searchTerm) ||
        ticket.system_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Advanced filters
    if (advancedFilters.status) {
      if(advancedFilters.status === 'OPEN') {
        filtered = filtered.filter(t => [
          TICKET_STATUSES.PENDING_ASSIGNMENT,
          TICKET_STATUSES.ASSIGNED,
          TICKET_STATUSES.REASSIGNED,
          TICKET_STATUSES.WORK_IN_PROGRESS,
          TICKET_STATUSES.PENDING,
          TICKET_STATUSES.REOPENED
        ].includes(t.status));
      } else if (advancedFilters.status === 'CLOSED') {
        filtered = filtered.filter(t =>
          t.status === 'CLOSED'
        );
        if (advancedFilters.closedToday) {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const todayEnd = new Date();
          todayEnd.setHours(23, 59, 59, 999);
          filtered = filtered.filter(t =>
            t.closed_at &&
            new Date(t.closed_at) >= todayStart &&
            new Date(t.closed_at) <= todayEnd
          );
        }
      }
      else {
      filtered = filtered.filter(t => t.status === advancedFilters.status);
      }
    }
    if (advancedFilters.priority) {
      filtered = filtered.filter(t => t.priority === advancedFilters.priority);
      if(advancedFilters.activeOnly){
        filtered = filtered.filter(t => t.status !== 'CLOSED');
      } 
    }
    
    if (advancedFilters.assignee) {
      filtered = filtered.filter(t => t.assignee_id?.toString() === advancedFilters.assignee);
    }
    if (advancedFilters.site) {
      filtered = filtered.filter(t => t.site_id?.toString() === advancedFilters.site);
    }
    if (advancedFilters.startDate) {
      filtered = filtered.filter(t => new Date(t.created_at) >= new Date(advancedFilters.startDate));
    }
    if (advancedFilters.endDate) {
      const endDate = new Date(advancedFilters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => new Date(t.created_at) <= endDate);
    }

  if (advancedFilters.overdue) {
    const slaMs = 48 * 60 * 60 * 1000;
    const now = new Date();
    filtered = filtered.filter(t => {
      const createdAt = parseBackendDate(t.created_at);
      if (!createdAt) return false;
      const isActive = [
        TICKET_STATUSES.PENDING_ASSIGNMENT,
        TICKET_STATUSES.ASSIGNED,
        TICKET_STATUSES.REASSIGNED,
        TICKET_STATUSES.WORK_IN_PROGRESS,
        TICKET_STATUSES.PENDING,
        TICKET_STATUSES.REOPENED
      ].includes(t.status);
      return isActive && (now - createdAt) > slaMs;
    });
    }

    setFilteredTickets(filtered);
  };

  const handleTicketCardClick = (filter) => {
    setSearchTerm('');

    if (filter === 'ALL') {
      setAdvancedFilters({});
      setSearchParams({});
    } else if (filter === 'OPEN') {
      setAdvancedFilters({ status: 'OPEN' });
      setSearchParams({ status: 'OPEN' });
    } else if (filter === 'CLOSED') {
      setAdvancedFilters({ status: 'CLOSED' });
      setSearchParams({ status: 'CLOSED' });
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const handleAddTicket = async (ticketData, attachments = []) => {
    const result = await ticketService.createTicket(ticketData);
    
    if (result.success) {
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          await ticketService.uploadAttachment(result.data.id, attachment.file);
        }
        success('Ticket created with attachments!');
      } else {
        success('Ticket created successfully!');
      }
      
      await loadTickets();
    } else {
      error(result.error || 'Failed to create ticket');
    }
  };

  const handleDownloadReport = async (ticket) => {
    // Notify user that download is starting
    success('Starting report download...');
    const result = await ticketService.downloadServiceReport(ticket.id);
    if (!result.success) {
      error('Failed to download report: ' + result.error);
    }
  };

  const handleExport = async () => {
    const result = await ticketService.exportTicketsCSV(advancedFilters);
    if (result?.success !== false) {
      success('Tickets exported successfully!');
    } else {
      error(result.error || 'Failed to export tickets');
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters({});
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type}
          </div>
        </div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value]}20`,
          color: PRIORITY_COLORS[value]
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value]}20`,
          color: STATUS_COLORS[value]
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'assignee_name',
      label: 'Assignee',
      render: (value) => value || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Unassigned</span>
    },
    {
      key: 'site_name',
      label: 'Site'
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDateTime(value)
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading tickets...
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Stats Cards */}
      <div style={gridStyle}>
        <StatCard
          title="Total Tickets"
          value={tickets.length}
          icon=""
          color="#3b82f6"
          onClick={() => handleTicketCardClick('ALL')}
        />
        <StatCard
          title="Showing"
          value={filteredTickets.length}
          icon=""
          subtitle="After filters"
          color="var(--primary)"
        />
        <StatCard
          title="Open"
          value={tickets.filter(t => [
            TICKET_STATUSES.PENDING_ASSIGNMENT,
            TICKET_STATUSES.ASSIGNED,
            TICKET_STATUSES.REASSIGNED,
            TICKET_STATUSES.WORK_IN_PROGRESS,
            TICKET_STATUSES.PENDING,
            TICKET_STATUSES.REOPENED
          ].includes(t.status)).length}
          icon=""
          subtitle="Active tickets"
          color="#f59e0b"
          onClick={() => handleTicketCardClick('OPEN')}
        />
        <StatCard
          title="Closed"
          value={tickets.filter(t => t.status === 'CLOSED').length}
          icon=""
          subtitle="Completed"
          color="#10b981"
          onClick={() => handleTicketCardClick('CLOSED')}
        />
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <AdvancedFilters
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            engineers={engineers}
            sites={sites}
          />
          <Button variant="secondary" onClick={loadTickets}>
             Refresh
          </Button>
          <Button variant="secondary" onClick={handleExport}>
             Export CSV
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
             New Ticket
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredTickets}
        onRowClick={handleViewTicket}
        actions={[
          { label: ' View', onClick: handleViewTicket },
          { 
            label: ' View Report', 
            onClick: handleDownloadReport,
            // Only show View Report if status is PENDING or CLOSED
            condition: (ticket) => ticket.status === TICKET_STATUSES.PENDING || ticket.status === TICKET_STATUSES.CLOSED
          }
        ]}
        pageSize={15}
      />

      {/* Modals */}
      <ViewTicketModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
      />

      <AddTicketModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTicket}
      />
    </div>
  );
};

export default TicketsView;